import type { Point, Wall, Room, WallWithTempId } from '../types';
import { isNearPoint, pointsAreEqual, getRoomBounds, getRoomCenter, generateStableWallId } from './floorPlanUtils';
import { getPointId } from './pointDecoupling';

/**
 * Professional-grade partial room dragging system
 * Allows dragging disconnected parts of rooms independently
 */

export interface WallGroup {
  walls: Wall[];
  isConnectedToOtherRooms: boolean;
  connectedRoomIds: string[];
  groupId: string;
  bounds: { minX: number; maxX: number; minY: number; maxY: number };
}

export interface PartialRoomDragResult {
  canDragPartially: boolean;
  disconnectedGroups: WallGroup[];
  connectedGroups: WallGroup[];
  selectedGroup?: WallGroup;
}

/**
 * Analyze room connectivity to find disconnected parts
 */
export const analyzeRoomConnectivity = (
  targetRoom: Room,
  allRooms: Room[],
  connectionTolerance: number = 1.0
): PartialRoomDragResult => {
  console.log('🔍 PARTIAL ROOM ANALYSIS: Starting connectivity analysis', {
    targetRoom: targetRoom.name,
    totalRooms: allRooms.length,
    targetWallCount: targetRoom.walls.length
  });

  // Find other rooms (excluding the target room)
  const otherRooms = allRooms.filter(room => room.stableId !== targetRoom.stableId);
  
  // Group walls by connectivity
  const wallGroups: WallGroup[] = [];
  const processedWalls = new Set<string>();
  
  for (const wall of targetRoom.walls) {
    const wallId = getWallId(wall);
    if (processedWalls.has(wallId)) continue;
    
    // Find connected walls in the same room (forming a continuous segment)
    const connectedWallsInRoom = findConnectedWallsInRoom(wall, targetRoom.walls, processedWalls);
    
    // Check if this wall group connects to other rooms
    const connectionAnalysis = analyzeWallGroupConnections(connectedWallsInRoom, otherRooms, connectionTolerance);
    
    const group: WallGroup = {
      walls: connectedWallsInRoom,
      isConnectedToOtherRooms: connectionAnalysis.isConnected,
      connectedRoomIds: connectionAnalysis.connectedRoomIds,
      groupId: `group-${wallGroups.length}`,
      bounds: calculateWallGroupBounds(connectedWallsInRoom)
    };
    
    wallGroups.push(group);
    
    // Mark all walls in this group as processed
    connectedWallsInRoom.forEach(w => processedWalls.add(getWallId(w)));
    
    console.log('🔗 WALL GROUP IDENTIFIED:', {
      groupId: group.groupId,
      wallCount: group.walls.length,
      isConnected: group.isConnectedToOtherRooms,
      connectedRooms: group.connectedRoomIds
    });
  }
  
  const disconnectedGroups = wallGroups.filter(group => !group.isConnectedToOtherRooms);
  const connectedGroups = wallGroups.filter(group => group.isConnectedToOtherRooms);
  
  const result: PartialRoomDragResult = {
    canDragPartially: disconnectedGroups.length > 0 && connectedGroups.length > 0,
    disconnectedGroups,
    connectedGroups
  };
  
  console.log('✅ CONNECTIVITY ANALYSIS COMPLETE:', {
    canDragPartially: result.canDragPartially,
    disconnectedGroups: disconnectedGroups.length,
    connectedGroups: connectedGroups.length,
    scenario: result.canDragPartially ? 'PARTIAL_DRAG_POSSIBLE' : 'FULL_ROOM_ONLY'
  });
  
  return result;
};

/**
 * Select which part of the room to drag based on user interaction
 */
export const selectRoomPartForDragging = (
  clickPoint: Point,
  connectivityAnalysis: PartialRoomDragResult,
  selectionTolerance: number = 20
): WallGroup | null => {
  // Only allow selection of disconnected groups
  for (const group of connectivityAnalysis.disconnectedGroups) {
    if (isPointNearWallGroup(clickPoint, group.walls, selectionTolerance)) {
      console.log('🎯 PARTIAL ROOM SELECTED:', {
        groupId: group.groupId,
        wallCount: group.walls.length,
        clickPoint,
        groupBounds: group.bounds
      });
      return group;
    }
  }
  
  return null;
};

/**
 * Perform partial room dragging
 */
export const dragRoomPartially = (
  selectedGroup: WallGroup,
  deltaX: number,
  deltaY: number,
  targetRooms: Room[],
  autoConnectThreshold: number = 25
): {
  movedWalls: Wall[];
  autoConnectionResult?: {
    shouldConnect: boolean;
    targetRoom: Room;
    connectionPoint: Point;
  };
} => {
  // Move the selected wall group
  const movedWalls = selectedGroup.walls.map(wall => ({
    ...wall,
    start: { x: wall.start.x + deltaX, y: wall.start.y + deltaY },
    end: { x: wall.end.x + deltaX, y: wall.end.y + deltaY }
  }));
  
  // Check for auto-connection opportunities
  const autoConnectionResult = checkAutoConnectionOpportunity(
    movedWalls,
    targetRooms,
    autoConnectThreshold
  );
  
  console.log('🏃 PARTIAL ROOM DRAGGED:', {
    groupId: selectedGroup.groupId,
    delta: { x: deltaX, y: deltaY },
    wallsMoved: movedWalls.length,
    autoConnection: autoConnectionResult?.shouldConnect || false
  });
  
  return {
    movedWalls,
    autoConnectionResult
  };
};

/**
 * Create connection between partial room and target room
 */
export const connectPartialRoomToTarget = (
  partialRoomWalls: Wall[],
  targetRoom: Room,
  connectionPoint: Point
): Wall[] => {
  // Find the closest point on the partial room to the connection point
  let closestWall: Wall | null = null;
  let closestPoint: Point | null = null;
  let minDistance = Infinity;
  
  partialRoomWalls.forEach(wall => {
    const wallMidpoint = {
      x: (wall.start.x + wall.end.x) / 2,
      y: (wall.start.y + wall.end.y) / 2
    };
    
    const distance = Math.hypot(
      wallMidpoint.x - connectionPoint.x,
      wallMidpoint.y - connectionPoint.y
    );
    
    if (distance < minDistance) {
      minDistance = distance;
      closestWall = wall;
      closestPoint = wallMidpoint;
    }
  });
  
  if (!closestWall || !closestPoint) {
    return partialRoomWalls;
  }
  
  // Create connecting wall (we know closestWall is not null due to the check above)
  const referenceWall = closestWall as Wall;
  const connectingWall: Wall = {
    start: closestPoint,
    end: connectionPoint,
    type: 'None',
    floor: referenceWall.floor
  };
  
  console.log('🔗 PARTIAL ROOM CONNECTION CREATED:', {
    from: closestPoint,
    to: connectionPoint,
    distance: minDistance
  });
  
  return [...partialRoomWalls, connectingWall];
};

// Helper functions

const getWallId = (wall: Wall): string => {
  // 🔧 FIX: Use generateStableWallId for consistency with wallSegmentation.ts
  // This ensures all wall identification uses the same algorithm
  return generateStableWallId(wall.start, wall.end);
  
  // REMOVED: Simple coordinate string that was inconsistent
  // return `${wall.start.x},${wall.start.y}-${wall.end.x},${wall.end.y}`;
};

const findConnectedWallsInRoom = (
  startWall: Wall,
  roomWalls: Wall[],
  processedWalls: Set<string>
): Wall[] => {
  const connectedWalls: Wall[] = [startWall];
  const toProcess: Wall[] = [startWall];
  processedWalls.add(getWallId(startWall));
  
  while (toProcess.length > 0) {
    const currentWall = toProcess.shift()!;
    
    // Find walls that share endpoints with current wall
    for (const wall of roomWalls) {
      const wallId = getWallId(wall);
      if (processedWalls.has(wallId)) continue;
      
      const isConnected = 
        pointsAreEqual(currentWall.start, wall.start, 0.1) ||
        pointsAreEqual(currentWall.start, wall.end, 0.1) ||
        pointsAreEqual(currentWall.end, wall.start, 0.1) ||
        pointsAreEqual(currentWall.end, wall.end, 0.1);
      
      if (isConnected) {
        connectedWalls.push(wall);
        toProcess.push(wall);
        processedWalls.add(wallId);
      }
    }
  }
  
  return connectedWalls;
};

const analyzeWallGroupConnections = (
  wallGroup: Wall[],
  otherRooms: Room[],
  tolerance: number
): { isConnected: boolean; connectedRoomIds: string[] } => {
  const connectedRoomIds: string[] = [];
  
  for (const room of otherRooms) {
    for (const groupWall of wallGroup) {
      for (const roomWall of room.walls) {
        // Check if walls share an endpoint or overlap
        const hasSharedEndpoint = 
          pointsAreEqual(groupWall.start, roomWall.start, tolerance) ||
          pointsAreEqual(groupWall.start, roomWall.end, tolerance) ||
          pointsAreEqual(groupWall.end, roomWall.start, tolerance) ||
          pointsAreEqual(groupWall.end, roomWall.end, tolerance);
        
        if (hasSharedEndpoint && room.stableId) {
          connectedRoomIds.push(room.stableId);
          break;
        }
      }
      if (connectedRoomIds.includes(room.stableId!)) break;
    }
  }
  
  return {
    isConnected: connectedRoomIds.length > 0,
    connectedRoomIds: Array.from(new Set(connectedRoomIds))
  };
};

const calculateWallGroupBounds = (walls: Wall[]): { minX: number; maxX: number; minY: number; maxY: number } => {
  if (walls.length === 0) {
    return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
  }
  
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  
  walls.forEach(wall => {
    minX = Math.min(minX, wall.start.x, wall.end.x);
    maxX = Math.max(maxX, wall.start.x, wall.end.x);
    minY = Math.min(minY, wall.start.y, wall.end.y);
    maxY = Math.max(maxY, wall.start.y, wall.end.y);
  });
  
  return { minX, maxX, minY, maxY };
};

const isPointNearWallGroup = (point: Point, walls: Wall[], tolerance: number): boolean => {
  return walls.some(wall => {
    const distToStart = Math.hypot(point.x - wall.start.x, point.y - wall.start.y);
    const distToEnd = Math.hypot(point.x - wall.end.x, point.y - wall.end.y);
    
    // Check distance to endpoints
    if (distToStart < tolerance || distToEnd < tolerance) {
      return true;
    }
    
    // Check distance to wall line
    const wallLength = Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y);
    if (wallLength === 0) return false;
    
    const t = Math.max(0, Math.min(1,
      ((point.x - wall.start.x) * (wall.end.x - wall.start.x) +
       (point.y - wall.start.y) * (wall.end.y - wall.start.y)) / (wallLength * wallLength)
    ));
    
    const projectedPoint = {
      x: wall.start.x + t * (wall.end.x - wall.start.x),
      y: wall.start.y + t * (wall.end.y - wall.start.y)
    };
    
    const distToLine = Math.hypot(point.x - projectedPoint.x, point.y - projectedPoint.y);
    return distToLine < tolerance;
  });
};

const checkAutoConnectionOpportunity = (
  movedWalls: Wall[],
  targetRooms: Room[],
  threshold: number
): { shouldConnect: boolean; targetRoom: Room; connectionPoint: Point } | undefined => {
  // Find the center of the moved wall group
  const groupBounds = calculateWallGroupBounds(movedWalls);
  const groupCenter = {
    x: (groupBounds.minX + groupBounds.maxX) / 2,
    y: (groupBounds.minY + groupBounds.maxY) / 2
  };
  
  // Check each target room for proximity
  for (const room of targetRooms) {
    const roomBounds = getRoomBounds(room.walls);
    const roomCenter = getRoomCenter(room.walls);
    
    const distance = Math.hypot(
      groupCenter.x - roomCenter.x,
      groupCenter.y - roomCenter.y
    );
    
    if (distance < threshold) {
      // Find the closest point on the room boundary
      const connectionPoint = findClosestPointOnRoomBoundary(groupCenter, room);
      
      return {
        shouldConnect: true,
        targetRoom: room,
        connectionPoint
      };
    }
  }
  
  return undefined;
};

const findClosestPointOnRoomBoundary = (point: Point, room: Room): Point => {
  let closestPoint = point;
  let minDistance = Infinity;
  
  room.walls.forEach(wall => {
    const wallLength = Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y);
    if (wallLength === 0) return;
    
    const t = Math.max(0, Math.min(1,
      ((point.x - wall.start.x) * (wall.end.x - wall.start.x) +
       (point.y - wall.start.y) * (wall.end.y - wall.start.y)) / (wallLength * wallLength)
    ));
    
    const projectedPoint = {
      x: wall.start.x + t * (wall.end.x - wall.start.x),
      y: wall.start.y + t * (wall.end.y - wall.start.y)
    };
    
    const distance = Math.hypot(point.x - projectedPoint.x, point.y - projectedPoint.y);
    
    if (distance < minDistance) {
      minDistance = distance;
      closestPoint = projectedPoint;
    }
  });
  
  return closestPoint;
};

/**
 * Decouple a point if it's shared between rooms
 * This creates independent copies of shared points to allow independent room movement
 */
export const decouplePointIfShared = (
  point: Point,
  draggedSegment: Wall,
  draggedRoom: Room,
  allRooms: Room[],
  generateTempId: () => string
): { decoupledSegment: Wall; updatedRooms: Room[] } => {
  const updatedRooms: Room[] = [];
  let decoupledSegment = { ...draggedSegment };
  
  // Find rooms that share this point
  const sharingRooms = allRooms.filter(room => {
    if (room.stableId === draggedRoom.stableId) return false;
    
    return room.walls.some(wall => 
      isNearPoint(wall.start, point, 0.1) || isNearPoint(wall.end, point, 0.1)
    );
  });
  
  if (sharingRooms.length > 0) {
    console.log('🔄 POINT DECOUPLING:', {
      point,
      sharingRoomsCount: sharingRooms.length,
      draggedRoom: draggedRoom.name
    });
    
    // Create slightly offset point for the dragged segment
    const offset = 0.1;
    const decoupledPoint = {
      x: point.x + offset,
      y: point.y + offset
    };
    
    // Update the dragged segment with decoupled point
    if (pointsAreEqual(draggedSegment.start, point, 0.1)) {
      decoupledSegment.start = decoupledPoint;
    }
    if (pointsAreEqual(draggedSegment.end, point, 0.1)) {
      decoupledSegment.end = decoupledPoint;
    }
    
    // Update other rooms to maintain their connections
    sharingRooms.forEach(room => {
      const updatedRoom = { ...room };
      updatedRoom.walls = room.walls.map(wall => {
        const updatedWall = { ...wall };
        // Keep the original point for other rooms
        return updatedWall;
      });
      updatedRooms.push(updatedRoom);
    });
  }
  
  return { decoupledSegment, updatedRooms };
};

interface WallSegment {
  id: string;
  start: Point;
  end: Point;
  isMovable: boolean;
  originalWallId: string;
  intersectionPoints: Point[];
}

/**
 * Split a wall at intersection points to create movable and fixed segments
 */
export const splitWallAtIntersections = (
  wall: WallWithTempId,
  intersectionPoints: Point[],
  tolerance: number = 0.5
): WallSegment[] => {
  const segments: WallSegment[] = [];
  
  // Sort intersection points along the wall's direction
  const wallVector = { x: wall.end.x - wall.start.x, y: wall.end.y - wall.start.y };
  const wallLength = Math.hypot(wallVector.x, wallVector.y);
  
  if (wallLength === 0) {
    return [{
      id: `${wall.tempId}_full`,
      start: wall.start,
      end: wall.end,
      isMovable: true,
      originalWallId: wall.tempId!,
      intersectionPoints: []
    }];
  }
  
  const wallUnit = { x: wallVector.x / wallLength, y: wallVector.y / wallLength };
  
  // Project intersection points onto the wall and sort by distance from start
  const projectedIntersections = intersectionPoints
    .map(point => {
      const pointVector = { x: point.x - wall.start.x, y: point.y - wall.start.y };
      const distance = pointVector.x * wallUnit.x + pointVector.y * wallUnit.y;
      return { point, distance };
    })
    .filter(({ distance }) => distance >= -tolerance && distance <= wallLength + tolerance)
    .sort((a, b) => a.distance - b.distance);
  
  // Create segments between intersection points
  let currentStart = wall.start;
  let segmentIndex = 0;
  
  for (const { point: intersectionPoint, distance } of projectedIntersections) {
    // Create segment from current start to intersection point
    if (Math.hypot(currentStart.x - intersectionPoint.x, currentStart.y - intersectionPoint.y) > tolerance) {
      segments.push({
        id: `${wall.tempId}_segment_${segmentIndex}`,
        start: { ...currentStart },
        end: { ...intersectionPoint },
        isMovable: true, // Will be determined later based on connections
        originalWallId: wall.tempId!,
        intersectionPoints: [intersectionPoint]
      });
      segmentIndex++;
    }
    
    currentStart = intersectionPoint;
  }
  
  // Create final segment from last intersection to wall end
  if (Math.hypot(currentStart.x - wall.end.x, currentStart.y - wall.end.y) > tolerance) {
    segments.push({
      id: `${wall.tempId}_segment_${segmentIndex}`,
      start: { ...currentStart },
      end: { ...wall.end },
      isMovable: true,
      originalWallId: wall.tempId!,
      intersectionPoints: projectedIntersections.length > 0 ? [currentStart] : []
    });
  }
  
  // If no intersections, return the full wall as one segment
  if (segments.length === 0) {
    segments.push({
      id: `${wall.tempId}_full`,
      start: wall.start,
      end: wall.end,
      isMovable: true,
      originalWallId: wall.tempId!,
      intersectionPoints: []
    });
  }
  
  console.log('🔪 WALL SPLIT INTO SEGMENTS', {
    originalWall: `${getPointId(wall.start)} -> ${getPointId(wall.end)}`,
    intersectionCount: intersectionPoints.length,
    segmentCount: segments.length,
    segments: segments.map(s => ({
      id: s.id,
      coords: `${getPointId(s.start)} -> ${getPointId(s.end)}`,
      isMovable: s.isMovable
    }))
  });
  
  return segments;
};

/**
 * Determine which wall segments can move freely vs which are connected to other rooms
 */
export const determineMovableSegments = (
  segments: WallSegment[],
  allWalls: WallWithTempId[],
  movingRoom: Room,
  allRooms: Room[],
  tolerance: number = 0.5
): WallSegment[] => {
  return segments.map(segment => {
    // Check if this segment's endpoints are connected to walls from other rooms
    const startConnected = isPointConnectedToOtherRooms(segment.start, allWalls, movingRoom, allRooms, tolerance);
    const endConnected = isPointConnectedToOtherRooms(segment.end, allWalls, movingRoom, allRooms, tolerance);
    
    // A segment is movable if neither endpoint is connected to other rooms
    const isMovable = !startConnected && !endConnected;
    
    console.log('🔍 SEGMENT MOVABILITY CHECK', {
      segmentId: segment.id,
      coords: `${getPointId(segment.start)} -> ${getPointId(segment.end)}`,
      startConnected,
      endConnected,
      isMovable
    });
    
    return {
      ...segment,
      isMovable
    };
  });
};

/**
 * Check if a point is connected to walls from other rooms
 */
const isPointConnectedToOtherRooms = (
  point: Point,
  allWalls: WallWithTempId[],
  movingRoom: Room,
  allRooms: Room[],
  tolerance: number = 0.5
): boolean => {
  // Find walls that use this point
  const wallsUsingPoint = allWalls.filter(wall => 
    Math.hypot(wall.start.x - point.x, wall.start.y - point.y) <= tolerance ||
    Math.hypot(wall.end.x - point.x, wall.end.y - point.y) <= tolerance
  );
  
  // Check if any of these walls belong to other rooms
  for (const wall of wallsUsingPoint) {
    for (const room of allRooms) {
      if (room.stableId === movingRoom.stableId) continue; // Skip the moving room
      
      const wallBelongsToRoom = room.walls.some(roomWall =>
        (Math.hypot(roomWall.start.x - wall.start.x, roomWall.start.y - wall.start.y) <= tolerance &&
         Math.hypot(roomWall.end.x - wall.end.x, roomWall.end.y - wall.end.y) <= tolerance) ||
        (Math.hypot(roomWall.start.x - wall.end.x, roomWall.start.y - wall.end.y) <= tolerance &&
         Math.hypot(roomWall.end.x - wall.start.x, roomWall.end.y - wall.start.y) <= tolerance)
      );
      
      if (wallBelongsToRoom) {
        return true; // Point is connected to another room
      }
    }
  }
  
  return false;
};

/**
 * Apply partial wall movement to only the movable segments
 */
export const applyPartialWallMovement = (
  segments: WallSegment[],
  deltaX: number,
  deltaY: number,
  targetPoint: Point,
  originalWall: WallWithTempId
): { movedWalls: Wall[]; fixedWalls: Wall[] } => {
  const movedWalls: Wall[] = [];
  const fixedWalls: Wall[] = [];
  
  // 🔍 COMPREHENSIVE LOGGING: Track segment processing and wall creation
  console.log('🔍 [applyPartialWallMovement] PROCESSING_SEGMENTS:', {
    totalSegments: segments.length,
    originalWall: `(${originalWall.start.x.toFixed(1)},${originalWall.start.y.toFixed(1)})->(${originalWall.end.x.toFixed(1)},${originalWall.end.y.toFixed(1)})`,
    originalWallOwnership: {
      originatingRoomId: originalWall.originatingRoomId || 'NONE',
      assignedRoomIds: originalWall.assignedRoomIds || 'NONE'
    },
    stackTrace: new Error().stack?.split('\n').slice(0, 5)
  });
  
  for (const segment of segments) {
    if (segment.isMovable) {
      // Move this segment
      const movedWall: Wall = {
        start: { x: segment.start.x + deltaX, y: segment.start.y + deltaY },
        end: { x: segment.end.x + deltaX, y: segment.end.y + deltaY },
        type: 'None',
        floor: originalWall.floor || 1,
        // 🔍 OWNERSHIP PRESERVATION: Transfer ownership from original wall
        originatingRoomId: originalWall.originatingRoomId,
        assignedRoomIds: originalWall.assignedRoomIds ? [...originalWall.assignedRoomIds] : undefined
      };
      
      // 🔍 COMPREHENSIVE LOGGING: Track moved wall creation
      console.log('🔍 [applyPartialWallMovement] CREATING_MOVED_WALL:', {
        segmentId: segment.id,
        coords: `(${movedWall.start.x.toFixed(1)},${movedWall.start.y.toFixed(1)})->(${movedWall.end.x.toFixed(1)},${movedWall.end.y.toFixed(1)})`,
        hasOwnership: !!(movedWall.originatingRoomId && movedWall.assignedRoomIds),
        originatingRoomId: movedWall.originatingRoomId || 'NONE',
        assignedRoomIds: movedWall.assignedRoomIds || 'NONE',
        originalCoords: `(${segment.start.x.toFixed(1)},${segment.start.y.toFixed(1)})->(${segment.end.x.toFixed(1)},${segment.end.y.toFixed(1)})`,
        delta: { x: deltaX, y: deltaY }
      });
      
      movedWalls.push(movedWall);
      
      console.log('✅ SEGMENT MOVED', {
        segmentId: segment.id,
        originalCoords: `${getPointId(segment.start)} -> ${getPointId(segment.end)}`,
        newCoords: `${getPointId(movedWall.start)} -> ${getPointId(movedWall.end)}`,
        delta: { x: deltaX, y: deltaY }
      });
    } else {
      // Keep this segment fixed
      const fixedWall: Wall = {
        start: { ...segment.start },
        end: { ...segment.end },
        type: 'None',
        floor: originalWall.floor || 1,
        // 🔍 OWNERSHIP PRESERVATION: Transfer ownership from original wall
        originatingRoomId: originalWall.originatingRoomId,
        assignedRoomIds: originalWall.assignedRoomIds ? [...originalWall.assignedRoomIds] : undefined
      };
      
      // 🔍 COMPREHENSIVE LOGGING: Track fixed wall creation
      console.log('🔍 [applyPartialWallMovement] CREATING_FIXED_WALL:', {
        segmentId: segment.id,
        coords: `(${fixedWall.start.x.toFixed(1)},${fixedWall.start.y.toFixed(1)})->(${fixedWall.end.x.toFixed(1)},${fixedWall.end.y.toFixed(1)})`,
        hasOwnership: !!(fixedWall.originatingRoomId && fixedWall.assignedRoomIds),
        originatingRoomId: fixedWall.originatingRoomId || 'NONE',
        assignedRoomIds: fixedWall.assignedRoomIds || 'NONE',
        reason: 'Connected to other rooms'
      });
      
      fixedWalls.push(fixedWall);
      
      console.log('🔒 SEGMENT FIXED', {
        segmentId: segment.id,
        coords: `${getPointId(segment.start)} -> ${getPointId(segment.end)}`,
        reason: 'Connected to other rooms'
      });
    }
  }
  
  console.log('🎯 PARTIAL WALL MOVEMENT COMPLETE', {
    originalWall: `${getPointId(originalWall.start)} -> ${getPointId(originalWall.end)}`,
    movedSegments: movedWalls.length,
    fixedSegments: fixedWalls.length,
    totalSegments: segments.length
  });
  
  return { movedWalls, fixedWalls };
};

/**
 * Main function: Handle partial wall dragging with intersection-based splitting
 */
export const handlePartialWallDrag = (
  wall: WallWithTempId,
  intersectionPoints: Point[],
  movingRoom: Room,
  allRooms: Room[],
  allWalls: WallWithTempId[],
  deltaX: number,
  deltaY: number,
  targetPoint: Point
): { 
  resultWalls: Wall[]; 
  partialMovementApplied: boolean;
  movableSegments: number;
  fixedSegments: number;
} => {
  console.log('🎯 STARTING PARTIAL WALL DRAG', {
    wall: `${getPointId(wall.start)} -> ${getPointId(wall.end)}`,
    intersectionCount: intersectionPoints.length,
    movingRoom: movingRoom.name,
    delta: { x: deltaX, y: deltaY }
  });
  
  // Step 1: Split wall at intersection points
  const segments = splitWallAtIntersections(wall, intersectionPoints);
  
  // Step 2: Determine which segments can move
  const segmentsWithMovability = determineMovableSegments(segments, allWalls, movingRoom, allRooms);
  
  // Step 3: Apply movement to movable segments only
  const { movedWalls, fixedWalls } = applyPartialWallMovement(
    segmentsWithMovability,
    deltaX,
    deltaY,
    targetPoint,
    wall
  );
  
  // Step 4: Combine all resulting walls
  const resultWalls = [...movedWalls, ...fixedWalls];
  
  // Step 5: Add back other walls from the room (excluding the original wall being moved)
  const otherRoomWalls = allWalls.filter(w => 
    w.tempId !== wall.tempId && 
    movingRoom.walls.some(roomWall =>
      (Math.hypot(roomWall.start.x - w.start.x, roomWall.start.y - w.start.y) <= 0.5 &&
       Math.hypot(roomWall.end.x - w.end.x, roomWall.end.y - w.end.y) <= 0.5) ||
      (Math.hypot(roomWall.start.x - w.end.x, roomWall.start.y - w.end.y) <= 0.5 &&
       Math.hypot(roomWall.end.x - w.start.x, roomWall.end.y - w.start.y) <= 0.5)
    )
  );
  
  otherRoomWalls.forEach(wall => {
    resultWalls.push({
      start: { ...wall.start },
      end: { ...wall.end },
      type: wall.type || 'None',
      floor: wall.floor || 1
    });
  });
  
  const partialMovementApplied = movedWalls.length > 0 && fixedWalls.length > 0;
  
  console.log('🏁 PARTIAL WALL DRAG COMPLETE', {
    originalWall: `${getPointId(wall.start)} -> ${getPointId(wall.end)}`,
    segmentsCreated: segments.length,
    movableSegments: movedWalls.length,
    fixedSegments: fixedWalls.length,
    partialMovementApplied,
    totalResultWalls: resultWalls.length
  });
  
  return {
    resultWalls,
    partialMovementApplied,
    movableSegments: movedWalls.length,
    fixedSegments: fixedWalls.length
  };
}; 